/**
 * In-iframe Checkout Modal (Phase B Iteration 282).
 *
 * Wird auf der Public Pricing-Seite getriggert, wenn ein User auf
 * „Jetzt kaufen" bei einem Punkt-Paket / Abo / Service klickt.
 *
 * Flow:
 *   Step 0 (auto): Bestehende Session via localStorage → 1-Click Checkout
 *   Step 1: E-Mail Eingabe → check-email
 *   Step 2a: OTP (Bestandskunde) — request via /auth/otp/request, verify via /verify
 *   Step 2b: Quick-Register (Neukunde) — /public/shop/quick-register
 *   Step 3: Payrexx-iframe-Embed + Polling /payment-status
 *   Step 4: Erfolg
 *
 * Multi-Studio: studio_id wird durchgereicht.
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { X, ArrowRight, CheckCircle2, Mail, Loader2, Building2 } from 'lucide-react';
import { et, EMBED_LANG } from '../../i18n/embedLang';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const COLORS = {
  bg: '#0a0a0a',
  card: '#171717',
  border: '#262626',
  text: '#FFFFFF',
  textMuted: '#A3A3A3',
  textSubtle: '#737373',
  accent: '#EAB308',
  accentHover: '#FACC15',
  error: '#EF4444',
  success: '#10B981',
};

const headingFont = "'Space Grotesk', sans-serif";
const fontFamily = "'Inter', sans-serif";

const STORAGE_KEY = 'sf_shop_session';

const saveSession = (data) => {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...data, ts: Date.now() })); } catch (e) {}
};
const loadSession = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const d = JSON.parse(raw);
    // 30 Tage gültig
    if (Date.now() - (d.ts || 0) > 30 * 24 * 3600 * 1000) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return d;
  } catch (e) { return null; }
};
const clearSession = () => { try { localStorage.removeItem(STORAGE_KEY); } catch (e) {} };

const ShopCheckoutModal = ({ open, onClose, item, studio, currency = 'CHF' }) => {
  const [step, setStep] = useState('init');  // init | email | otp | register | payment | success | error
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [accept, setAccept] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [paymentUrl, setPaymentUrl] = useState('');
  const [orderToken, setOrderToken] = useState('');
  const [savedSession, setSavedSession] = useState(null);

  const pollTimer = useRef(null);

  // Auto-detect existing session on open → 1-Click
  useEffect(() => {
    if (!open) return;
    const session = loadSession();
    if (session && session.email) {
      setSavedSession(session);
      setEmail(session.email);
      setStep('init_oneclick');
    } else {
      setStep('email');
    }
    return () => {
      if (pollTimer.current) {
        clearInterval(pollTimer.current);
        pollTimer.current = null;
      }
    };
  }, [open]);

  // Reset state on close
  useEffect(() => {
    if (!open) {
      setStep('init');
      setEmail('');
      setFirstName('');
      setLastName('');
      setPhone('');
      setOtpCode('');
      setError('');
      setBusy(false);
      setPaymentUrl('');
      setOrderToken('');
      if (pollTimer.current) clearInterval(pollTimer.current);
    }
  }, [open]);

  const startPolling = useCallback((token) => {
    if (pollTimer.current) clearInterval(pollTimer.current);
    let attempts = 0;
    pollTimer.current = setInterval(async () => {
      attempts += 1;
      try {
        const { data } = await axios.get(`${API}/public/shop/payment-status/${token}`);
        if (data.paid && data.fulfilled) {
          clearInterval(pollTimer.current);
          pollTimer.current = null;
          setStep('success');
        }
      } catch (e) { /* ignore */ }
      // Stop after 5 Min (60 × 5s)
      if (attempts > 60) {
        clearInterval(pollTimer.current);
        pollTimer.current = null;
      }
    }, 5000);
  }, []);

  const initiateCheckout = useCallback(async (customerEmail, customerFirstName = null, customerLastName = null) => {
    setBusy(true);
    setError('');
    try {
      const { data } = await axios.post(`${API}/public/shop/checkout`, {
        item_type: item.type,
        item_id: item.id,
        studio_id: studio?.studio_id || null,
        customer_email: customerEmail,
        customer_first_name: customerFirstName,
        customer_last_name: customerLastName,
        quantity: 1,
      });
      setPaymentUrl(data.payment_link);
      setOrderToken(data.order_token);
      saveSession({ email: customerEmail });
      setStep('payment');
      startPolling(data.order_token);
    } catch (e) {
      setError(e.response?.data?.detail || et('Checkout konnte nicht gestartet werden'));
      setStep('error');
    } finally { setBusy(false); }
  }, [item, studio, startPolling]);

  // 1-Click for returning users
  const handleOneClick = () => initiateCheckout(savedSession.email);
  const handleUseDifferent = () => { clearSession(); setSavedSession(null); setStep('email'); setEmail(''); };

  const handleEmailNext = async () => {
    if (!email.includes('@')) { setError(et('Bitte gültige E-Mail eingeben')); return; }
    setBusy(true); setError('');
    try {
      const { data } = await axios.post(`${API}/public/shop/check-email`, { email });
      if (data.exists) {
        // OTP-Request
        await axios.post(`${API}/auth/otp/request`, { email });
        setStep('otp');
      } else {
        setStep('register');
      }
    } catch (e) {
      setError(e.response?.data?.detail || et('Fehler bei der Anfrage'));
    } finally { setBusy(false); }
  };

  const handleVerifyOtp = async () => {
    if (otpCode.length < 4) { setError(et('Bitte Code eingeben')); return; }
    setBusy(true); setError('');
    try {
      await axios.post(`${API}/auth/otp/verify`, { identifier: email, code: otpCode });
      await initiateCheckout(email);
    } catch (e) {
      setError(e.response?.data?.detail || et('Code ungültig oder abgelaufen'));
      setBusy(false);
    }
  };

  const handleRegister = async () => {
    if (!firstName || !lastName) { setError(et('Vor- und Nachname sind Pflicht')); return; }
    if (!accept) { setError(et('Bitte AGB akzeptieren')); return; }
    setBusy(true); setError('');
    try {
      await axios.post(`${API}/public/shop/quick-register`, {
        email, first_name: firstName, last_name: lastName,
        phone: phone || null, studio_id: studio?.studio_id || null,
        accept_terms: true,
      });
      await initiateCheckout(email, firstName, lastName);
    } catch (e) {
      setError(e.response?.data?.detail || et('Registrierung fehlgeschlagen'));
      setBusy(false);
    }
  };

  if (!open) return null;

  const price = item ? item.price : 0;

  return (
    <div
      data-testid="shop-checkout-modal"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.85)',
        backdropFilter: 'blur(8px)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        fontFamily,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div style={{
        background: COLORS.card,
        border: `1px solid ${COLORS.border}`,
        borderRadius: 16,
        width: '100%',
        maxWidth: step === 'payment' ? 720 : 480,
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 32px 80px rgba(0,0,0,0.5)',
        color: COLORS.text,
        position: 'relative',
      }}>
        {/* Close-Button */}
        <button
          onClick={() => onClose?.()}
          data-testid="checkout-close"
          style={{
            position: 'absolute', top: 12, right: 12,
            background: 'transparent', border: 'none',
            color: COLORS.textMuted, cursor: 'pointer', padding: 8,
          }}
          aria-label="Schliessen"
        ><X size={18} /></button>

        {/* Header — Item-Info */}
        <div style={{ padding: '24px 28px 16px', borderBottom: `1px solid ${COLORS.border}` }}>
          <div style={{ fontSize: 11, color: COLORS.textMuted, letterSpacing: '0.1em', textTransform: 'uppercase', fontFamily: headingFont, fontWeight: 600 }}>
            {et('Bestellung')}
          </div>
          <div style={{ fontFamily: headingFont, fontSize: 20, fontWeight: 700, marginTop: 4 }}>
            {item?.name || 'Produkt'}
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginTop: 8 }}>
            <span style={{ fontFamily: headingFont, fontSize: 28, fontWeight: 800, color: COLORS.accent }}>{currency} {Number(price).toFixed(2)}</span>
          </div>
          {studio && (
            <div style={{
              marginTop: 10, padding: '6px 10px', display: 'inline-flex', alignItems: 'center', gap: 6,
              background: `${COLORS.accent}15`, border: `1px solid ${COLORS.accent}30`,
              borderRadius: 999, fontSize: 11, color: COLORS.accent,
            }}>
              <Building2 size={11} />
              {studio.short_name || studio.name}
              <span style={{ color: COLORS.textMuted, marginLeft: 4 }}>{et('· in jedem Studio nutzbar')}</span>
            </div>
          )}
        </div>

        {/* Body */}
        <div style={{ padding: '24px 28px' }}>
          {step === 'init_oneclick' && savedSession && (
            <div data-testid="checkout-oneclick">
              <div style={{ fontSize: 14, color: COLORS.text, marginBottom: 16 }}>
                {et('Willkommen zurück! Mit dieser E-Mail fortfahren?')}
              </div>
              <div style={{ background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 10, padding: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
                <Mail size={14} color={COLORS.accent} />
                <span style={{ flex: 1, fontSize: 13 }}>{savedSession.email}</span>
              </div>
              <button
                onClick={handleOneClick}
                disabled={busy}
                data-testid="checkout-oneclick-btn"
                style={btnPrimary(busy)}
              >
                {busy ? <Loader2 size={14} className="spin" /> : et('Jetzt bezahlen')}
                {!busy && <ArrowRight size={14} />}
              </button>
              <button onClick={handleUseDifferent} style={btnGhost()}>{et('Andere E-Mail verwenden')}</button>
            </div>
          )}

          {step === 'email' && (
            <div data-testid="checkout-email">
              <label style={lbl()}>E-Mail-Adresse</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleEmailNext()}
                placeholder="deine@email.ch"
                data-testid="checkout-email-input"
                style={input()}
              />
              <p style={{ fontSize: 11, color: COLORS.textSubtle, marginTop: 8, lineHeight: 1.5 }}>
                {et('Wir prüfen ob du schon Mitglied bist. Falls ja, schicken wir dir einen Code per E-Mail / WhatsApp.')}
              </p>
              <button onClick={handleEmailNext} disabled={busy} data-testid="checkout-email-next" style={btnPrimary(busy)}>
                {busy ? <Loader2 size={14} className="spin" /> : <>{et('Weiter')} <ArrowRight size={14} /></>}
              </button>
            </div>
          )}

          {step === 'otp' && (
            <div data-testid="checkout-otp">
              <p style={{ fontSize: 13, color: COLORS.textMuted, marginBottom: 12, lineHeight: 1.5 }}>
                {et('Wir haben einen 6-stelligen Code an')} <strong style={{ color: COLORS.accent }}>{email}</strong>{EMBED_LANG === 'de' ? ' gesendet.' : '.'}
              </p>
              <label style={lbl()}>{et('Code')}</label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                onKeyDown={(e) => e.key === 'Enter' && handleVerifyOtp()}
                placeholder="123456"
                autoFocus
                data-testid="checkout-otp-input"
                style={{ ...input(), letterSpacing: 8, textAlign: 'center', fontSize: 22, fontFamily: headingFont }}
              />
              <button onClick={handleVerifyOtp} disabled={busy} data-testid="checkout-otp-verify" style={btnPrimary(busy)}>
                {busy ? <Loader2 size={14} className="spin" /> : <>{et('Bestätigen')} <ArrowRight size={14} /></>}
              </button>
              <button
                onClick={() => { setStep('email'); setOtpCode(''); }}
                style={btnGhost()}
              >
                {et('Andere E-Mail')}
              </button>
            </div>
          )}

          {step === 'register' && (
            <div data-testid="checkout-register">
              <p style={{ fontSize: 13, color: COLORS.textMuted, marginBottom: 12, lineHeight: 1.5 }}>
                {et('Noch kein Konto. Lass uns das in 30 Sekunden erledigen.')}
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div>
                  <label style={lbl()}>{et('Vorname')}</label>
                  <input value={firstName} onChange={(e) => setFirstName(e.target.value)} style={input()} data-testid="checkout-first-name" />
                </div>
                <div>
                  <label style={lbl()}>{et('Nachname')}</label>
                  <input value={lastName} onChange={(e) => setLastName(e.target.value)} style={input()} data-testid="checkout-last-name" />
                </div>
              </div>
              <label style={lbl()}>{et('Mobile (für WhatsApp-Codes)')}</label>
              <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+41 79 …" style={input()} data-testid="checkout-phone" />
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 14, fontSize: 12, color: COLORS.textMuted, cursor: 'pointer' }}>
                <input type="checkbox" checked={accept} onChange={(e) => setAccept(e.target.checked)} data-testid="checkout-accept-agb" />
                <span>{et('Ich akzeptiere die AGB und Datenschutzbestimmungen.')}</span>
              </label>
              <button onClick={handleRegister} disabled={busy} data-testid="checkout-register-btn" style={btnPrimary(busy)}>
                {busy ? <Loader2 size={14} className="spin" /> : <>{et('Konto anlegen & bezahlen')} <ArrowRight size={14} /></>}
              </button>
            </div>
          )}

          {step === 'payment' && paymentUrl && (
            <div data-testid="checkout-payment">
              <p style={{ fontSize: 13, color: COLORS.textMuted, marginBottom: 12, textAlign: 'center' }}>
                {et('🔒 Sichere Zahlung mit Payrexx')}
              </p>
              {/* Try iframe-embed first. Wenn Payrexx X-Frame-Options blockiert,
                  zeigen wir einen Link-Button als Fallback. */}
              <div style={{ border: `1px solid ${COLORS.border}`, borderRadius: 10, overflow: 'hidden', background: '#fff', height: 500 }}>
                <iframe
                  src={paymentUrl}
                  title="Payrexx Checkout"
                  style={{ width: '100%', height: '100%', border: 'none' }}
                  data-testid="payrexx-iframe"
                  allow="payment *; clipboard-read; clipboard-write"
                />
              </div>
              <p style={{ fontSize: 11, color: COLORS.textSubtle, marginTop: 10, textAlign: 'center' }}>
                {et('Zahlung läuft… Bei Problemen')}{' '}
                <a href={paymentUrl} target="_blank" rel="noopener noreferrer" style={{ color: COLORS.accent }}>
                  {et('in neuem Fenster öffnen')}
                </a>
              </p>
            </div>
          )}

          {step === 'success' && (
            <div data-testid="checkout-success" style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{
                width: 64, height: 64, borderRadius: '50%',
                background: `${COLORS.success}20`, display: 'inline-flex',
                alignItems: 'center', justifyContent: 'center', marginBottom: 16,
              }}>
                <CheckCircle2 size={32} color={COLORS.success} />
              </div>
              <div style={{ fontFamily: headingFont, fontSize: 22, fontWeight: 700, marginBottom: 8 }}>{et('Geschafft! 🎉')}</div>
              <p style={{ fontSize: 14, color: COLORS.textMuted, marginBottom: 20 }}>
                {et('Deine Bestellung ist eingegangen und')} {item?.type === 'point_package' ? et('die Punkte sind auf deinem Konto') : et('dein Abo ist aktiv')}.
              </p>
              <button onClick={() => onClose?.()} style={btnPrimary(false)} data-testid="checkout-success-close">{et('Fertig')}</button>
            </div>
          )}

          {step === 'error' && (
            <div data-testid="checkout-error" style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ color: COLORS.error, fontFamily: headingFont, fontSize: 18, fontWeight: 700, marginBottom: 8 }}>
                {et('Hoppla')}
              </div>
              <p style={{ fontSize: 13, color: COLORS.textMuted, marginBottom: 16 }}>{error}</p>
              <button onClick={() => setStep('email')} style={btnPrimary(false)}>{et('Erneut versuchen')}</button>
            </div>
          )}

          {error && step !== 'error' && (
            <p style={{ color: COLORS.error, fontSize: 12, marginTop: 12, textAlign: 'center' }}>{error}</p>
          )}
        </div>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        .spin { animation: spin 1s linear infinite; }
      `}</style>
    </div>
  );
};

// Style helpers
const lbl = () => ({
  display: 'block', fontSize: 11, fontWeight: 600,
  color: COLORS.textMuted, fontFamily: headingFont,
  letterSpacing: '0.06em', textTransform: 'uppercase',
  marginTop: 14, marginBottom: 6,
});

const input = () => ({
  width: '100%', boxSizing: 'border-box',
  padding: '11px 14px', borderRadius: 10,
  border: `1px solid ${COLORS.border}`,
  background: COLORS.bg, color: COLORS.text,
  fontSize: 14, fontFamily,
  outline: 'none',
});

const btnPrimary = (busy) => ({
  marginTop: 20, width: '100%',
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
  padding: '13px 20px',
  borderRadius: 10, border: 'none',
  background: busy ? COLORS.textSubtle : COLORS.accent,
  color: '#000', fontFamily: headingFont, fontWeight: 700,
  fontSize: 13, letterSpacing: '0.1em', textTransform: 'uppercase',
  cursor: busy ? 'wait' : 'pointer',
  transition: 'all .2s ease',
});

const btnGhost = () => ({
  marginTop: 10, width: '100%',
  background: 'transparent', border: 'none',
  color: COLORS.textMuted, fontSize: 12, cursor: 'pointer',
  textDecoration: 'underline',
});

export default ShopCheckoutModal;
